import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  BookOpen, Search, Warehouse, ArrowUpCircle, ArrowDownCircle, SlidersHorizontal, AlertTriangle,
  Download, FileText,
} from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";

const JENIS_META = {
  masuk: { l: "Masuk", c: "bg-emerald-50 text-emerald-700 border-emerald-200", i: ArrowUpCircle },
  keluar: { l: "Keluar", c: "bg-rose-50 text-rose-700 border-rose-200", i: ArrowDownCircle },
  opname_adjust: { l: "Penyesuaian", c: "bg-amber-50 text-amber-700 border-amber-200", i: SlidersHorizontal },
};

const inp = "h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

export default function KartuStok() {
  const { can } = useAuth();
  const bolehLaporan = can("inventory_laporan", "view");
  const bolehLookup = can("inventory_produk", "view");
  const bolehGudang = can("inventory_gudang", "view");
  const [searchParams] = useSearchParams();

  const [teks, setTeks] = useState("");
  const [saran, setSaran] = useState([]);
  const [sku, setSku] = useState((searchParams.get("sku") || "").trim().toUpperCase());
  const [gudang, setGudang] = useState([]);
  // Datang dari tautan "Kartu Stok" di Stok & Gudang (per SKU × gudang) — tanpa ini,
  // saldo yang tampil pertama kali gabungan semua gudang alih-alih gudang yang diklik.
  const [warehouseId, setWarehouseId] = useState(searchParams.get("warehouse_id") || "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Daftar gudang butuh izin modul terpisah (inventory_gudang) dari kartu stok
  // (inventory_laporan) — jangan panggil kalau tidak dipegang, pola yang sama
  // dipakai InventoryPO.jsx/InventoryStok.jsx supaya satu 403 tidak membuat
  // seluruh halaman kosong.
  useEffect(() => {
    if (!bolehGudang) return;
    api.get("/inventory/warehouses").then(r => setGudang(r.data.items || [])).catch(() => {});
  }, [bolehGudang]);

  const debounceRef = useRef(null);
  const lookupSeq = useRef(0);
  const cari = (nilai) => {
    setTeks(nilai);
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = nilai.trim();
    if (q.length < 2) { setSaran([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++lookupSeq.current;
      try {
        const r = await api.get("/inventory/products/lookup", { params: { q } });
        if (seq === lookupSeq.current) setSaran(r.data.items || []);
      } catch {
        if (seq === lookupSeq.current) setSaran([]);
      }
    }, 300);
  };

  const pilihProduk = (p) => {
    setSku(p.sku);
    setTeks("");
    setSaran([]);
  };

  const gantiSku = () => {
    setSku("");
    setData(null);
    setTeks("");
  };

  // Nomor urut request (pola yang sama dengan InventoryProducts.jsx): filter
  // beruntun (ganti gudang/tanggal cepat-cepat) tidak boleh membiarkan respons
  // yang datang belakangan menimpa hasil filter yang aktif sekarang.
  const reqSeq = useRef(0);
  const load = async () => {
    if (!sku || !bolehLaporan) { setData(null); return; }
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const r = await api.get("/inventory/kartu-stok", {
        params: {
          sku,
          warehouse_id: warehouseId || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        },
      });
      if (seq !== reqSeq.current) return;
      setData(r.data);
    } catch (e) {
      if (seq === reqSeq.current) {
        toast.error(e?.response?.data?.detail || "Gagal memuat kartu stok");
        setData(null);
      }
    } finally { if (seq === reqSeq.current) setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [sku, warehouseId, dateFrom, dateTo]);

  // Unduh file lewat fetch mentah (bukan `api` axios) — pola yang SAMA dengan
  // `InventoryStok.jsx::exportLaporan`: butuh respons blob, bukan JSON, dan
  // token diselipkan manual karena ini bukan lewat instance axios yang sudah
  // dikonfigurasi interceptor-nya.
  const unduh = async (path, namaFile) => {
    if (!sku) return;
    try {
      const token = localStorage.getItem("hris_token");
      const params = new URLSearchParams({ sku });
      if (warehouseId) params.set("warehouse_id", warehouseId);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await fetch(`${API}${path}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = namaFile;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Gagal mengunduh kartu stok");
    }
  };
  const unduhExcel = () => unduh("/inventory/kartu-stok/export", `Kartu_Stok_${sku}.xlsx`);
  const unduhPdf = () => unduh("/inventory/kartu-stok/export-pdf", `Kartu_Stok_${sku}.pdf`);

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="kartu-stok">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-emerald-600" /> Kartu Stok
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Mutasi satu produk urut waktu, lengkap dengan saldo berjalan — seperti kartu stok kertas konvensional.
          </p>
        </div>
        {bolehLaporan && sku && data && (
          <div className="flex items-center gap-2">
            <button onClick={unduhExcel} data-testid="ks-export-excel"
                    className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> Excel
            </button>
            <button onClick={unduhPdf} data-testid="ks-export-pdf"
                    className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
              <FileText className="w-4 h-4" /> PDF
            </button>
          </div>
        )}
      </div>

      {!bolehLaporan && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2"
             data-testid="ks-noizin">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          Akun Anda belum punya izin modul Laporan Inventory, jadi kartu stok tidak bisa dibuka dari sini.
        </div>
      )}

      {bolehLaporan && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={teks} onChange={e => cari(e.target.value)}
                     onKeyDown={e => {
                       if (e.key === "Enter" && teks.trim()) { setSku(teks.trim().toUpperCase()); setSaran([]); }
                     }}
                     placeholder="Cari SKU atau nama produk..." data-testid="ks-sku"
                     className={`${inp} pl-9 w-full`} />
              {sku && (
                <span data-testid="ks-sku-badge"
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                  SKU {sku}{data?.header?.nama ? ` — ${data.header.nama}` : ""}
                  <button type="button" onClick={gantiSku} data-testid="ks-sku-ganti"
                          title="Lepas — pilih produk lain"
                          className="text-emerald-600 hover:text-emerald-900 leading-none">×</button>
                </span>
              )}
              {saran.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                  {saran.map(p => (
                    <button key={p.sku} type="button" onClick={() => pilihProduk(p)} data-testid={`ks-saran-${p.sku}`}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-50 last:border-0">
                      <span className="font-mono text-xs text-slate-500">{p.sku}</span> · {p.nama}
                      <span className="block text-[11px] text-slate-400">stok {p.stock_qty}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {gudang.length > 0 && (
              <select value={warehouseId} onChange={e => setWarehouseId(e.target.value)}
                      data-testid="ks-gudang" aria-label="Filter gudang"
                      className={inp}>
                <option value="">Semua gudang</option>
                {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
              </select>
            )}
            <DatePicker value={dateFrom} onChange={setDateFrom}
                   data-testid="ks-dari" aria-label="Dari tanggal" />
            <span className="text-slate-400 text-xs">s/d</span>
            <DatePicker value={dateTo} onChange={setDateTo}
                   data-testid="ks-sampai" aria-label="Sampai tanggal" />
          </div>
        </div>
      )}

      {bolehLaporan && !sku && (
        <p className="text-sm text-slate-400 text-center py-12">Cari lalu pilih produk untuk melihat kartu stoknya.</p>
      )}

      {bolehLaporan && sku && (
        <>
          {data?.header && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm md:col-span-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Produk</p>
                <p className="font-display text-lg font-bold text-slate-900 mt-1">{data.header.nama}</p>
                <p className="text-xs text-slate-500 mt-0.5 font-mono">{data.header.sku}</p>
                <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
                  <Warehouse className="w-3.5 h-3.5" /> {data.header.gudang}
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Saldo Akhir</p>
                <p className="font-display text-2xl font-bold text-slate-900 mt-1">{data.header.saldo_akhir}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">unit dalam satuan kecil</p>
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto" data-testid="ks-tabel">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr className="text-left">
                  <th className="px-3 py-2.5 font-semibold">Tanggal</th>
                  <th className="px-3 py-2.5 font-semibold">Jenis</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Qty</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Saldo Berjalan</th>
                  <th className="px-3 py-2.5 font-semibold">Referensi</th>
                  <th className="px-3 py-2.5 font-semibold">Gudang</th>
                  <th className="px-3 py-2.5 font-semibold">PIC</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Nilai Modal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <LoadingState
                  loading={loading}
                  kosong={!loading && (!data || data.items.length === 0)}
                  pesanKosong="Tidak ada mutasi pada rentang ini."
                  varian="tabel"
                  kolom={8}
                  data-testid="ks-loading"
                />
                {!loading && data && data.items.map((it, i) => {
                  const meta = JENIS_META[it.jenis] || { l: it.jenis, c: "bg-slate-100 text-slate-600 border-slate-200", i: SlidersHorizontal };
                  const Ikon = meta.i;
                  return (
                    <tr key={i} data-testid={`ks-baris-${i}`} className="hover:bg-slate-50/60">
                      <td className="px-3 py-2.5 text-slate-800">{it.tanggal}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[11px] px-2 py-0.5 rounded border inline-flex items-center gap-1 ${meta.c}`}>
                          <Ikon className="w-3 h-3" /> {meta.l}
                        </span>
                      </td>
                      <td className={`px-3 py-2.5 text-right font-mono font-semibold ${it.qty >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {it.qty >= 0 ? `+${it.qty}` : it.qty}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-900">{it.saldo_berjalan}</td>
                      <td className="px-3 py-2.5">
                        <p className="font-mono text-[11px] text-slate-500">{it.code || "—"}</p>
                        {it.keterangan && <p className="text-[11px] text-slate-400 truncate max-w-[220px]">{it.keterangan}</p>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600">{it.warehouse_nama || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-600">{it.oleh || "—"}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-slate-900">
                        {it.nilai != null ? idr(it.nilai) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
